<template>
  <b-modal ref="document-tasks-modal"
           modal-class="task-modal"
           size="lg"
           @hide="editing = false"
  >
    <template #modal-header>
      <div class="" style="width: 100%;">
        <span @click="hide" class="btn btn-secondary cursor-pointer float-right ml-2 mr-0">
          <font-awesome-icon icon="times"/>
        </span>
        <h5 class="mb-1 text-center" data-qid="document-tasks-title">{{ $t('projects.engagement.evidence.tasks_title') }}</h5>
      </div>
    </template>

    <p class="text-muted mb-2">{{ file.file_name }}</p>

    <!-- Tasks the file is attached or linked to; each can be opened -->
    <div v-if="!editing" data-qid="document-tasks-list">
      <div v-for="task in fileTasks" :key="'document-task-' + task.id" class="document-tasks__row">
        <span class="document-tasks__title text-truncate">{{ task.title }}</span>
        <span class="document-tasks__kind text-muted">
          {{ file.owner_task && file.owner_task.id === task.id ? $t('projects.engagement.evidence.uploaded_here') : $t('projects.engagement.evidence.linked_here') }}
        </span>
        <span class="document-tasks__open">
          <AssessmentGlobalSingleTaskModal v-if="loadedTasks[task.id]"
                                           :task="loadedTasks[task.id]"
                                           :fromHomepage="true"
                                           @reloadContent="$emit('changed')"
          ></AssessmentGlobalSingleTaskModal>
          <font-awesome-icon v-else icon="spinner" class="fa-spin text-muted"/>
        </span>
      </div>
      <div v-if="!fileTasks.length" class="text-muted">{{ $t('projects.engagement.evidence.not_linked') }}</div>
    </div>

    <!-- Edit the tasks an evidence file is linked to -->
    <div v-else data-qid="document-tasks-edit">
      <b-form-input v-model="taskSearch"
                    :placeholder="$t('projects.engagement.evidence.search_tasks')"
                    class="mb-2"
      ></b-form-input>
      <div class="evidence-library__task-list">
        <b-form-checkbox v-for="task in visibleTasks"
                         :key="'document-link-' + task.id"
                         v-model="selectedTaskIds"
                         :value="task.id"
                         :disabled="!!file.owner_task && file.owner_task.id === task.id"
                         class="evidence-library__task"
        >
          {{ task.title }}
        </b-form-checkbox>
      </div>
      <!-- Unlinking deletes the annotations auditors made on those tasks -->
      <div v-if="removedAnnotationCount" class="alert alert-warning mt-2 mb-0" data-qid="document-tasks-annotation-warning">
        {{ $t('annotations.unlink_warning', { count: removedAnnotationCount }) }}
      </div>
    </div>

    <template #modal-footer>
      <div class="" style="width: 100%;">
        <div class="alert alert-menu-color color-secondary mb-0" role="alert">
          <div class="row">
            <div class="col-sm-6 text-muted pt-2">
              <span v-if="editing">{{ $t('projects.engagement.evidence.selected_count', { count: selectedTaskIds.length }) }}</span>
            </div>
            <div class="col-sm-6 text-right">
              <template v-if="!editing">
                <b-button variant="secondary" @click="hide">{{ $t('processes.policies.view.close') }}</b-button>
                <b-button v-if="canLink"
                          class="ml-3"
                          variant="success"
                          @click="startEditing"
                          data-qid="document-tasks-edit-button"
                >
                  {{ $t('projects.engagement.evidence.link_to_tasks') }}
                </b-button>
              </template>
              <template v-else>
                <b-button variant="secondary" @click="editing = false">{{ $t('projects.create.cancel') }}</b-button>
                <b-button class="ml-3"
                          variant="success"
                          :disabled="buttonDisabled"
                          @click="saveLinks"
                          data-qid="document-tasks-save"
                >
                  {{ $t('projects.create.submit') }}
                </b-button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import piincHttp from '@/util/configureAxios.js';

// Lists the tasks of this project a document belongs to, lets the user open them and (evidence) change the links
export default {
  name: 'DocumentTasksModal',
  props: {
    project: {
      type: Object,
      required: true
    },
    // All main tasks of the project: [{ id, title }]
    tasks: {
      type: Array,
      default: () => []
    },
    editable: {
      type: Boolean,
      default: false
    }
  },
  components: {
    // Loaded lazily to avoid a circular import through the task modal
    AssessmentGlobalSingleTaskModal: () => import('@/views/Tasks/AssessmentGlobalSingleTaskModal.vue')
  },
  data: function() {
    return {
      file: {},
      loadedTasks: {},
      editing: false,
      selectedTaskIds: [],
      taskSearch: '',
      buttonDisabled: false,
      annotationCounts: {}
    }
  },
  computed: {
    fileTasks() {
      let tasks = (this.file.linked_tasks || []).slice();
      if (this.file.owner_task) {
        tasks.unshift(this.file.owner_task);
      }
      return tasks;
    },
    // Working papers stay on their own task; evidence can be linked to any task
    canLink() {
      return this.editable && this.file.source !== 'working_paper';
    },
    removedAnnotationCount() {
      const removed = _.difference(this.file.linked_task_ids || [], this.selectedTaskIds);
      return _.sumBy(removed, id => this.annotationCounts[id] || 0);
    },
    visibleTasks() {
      const search = this.taskSearch.trim().toLowerCase();
      return search ? _.filter(this.tasks, task => (task.title || '').toLowerCase().includes(search)) : this.tasks;
    }
  },
  methods: {
    show(file) {
      this.file = file;
      this.editing = false;
      this.loadTasks();
      this.$refs['document-tasks-modal'].show();
    },
    hide() {
      this.$refs['document-tasks-modal'].hide();
    },
    loadTasks() {
      let self = this;
      _.forEach(this.fileTasks, task => {
        if (self.loadedTasks[task.id]) {
          return;
        }
        piincHttp.get('tasks/' + task.id).then(function(response) {
          self.$set(self.loadedTasks, task.id, response.data);
        }, function() {});
      });
    },
    startEditing() {
      this.selectedTaskIds = (this.file.linked_task_ids || []).slice();
      this.taskSearch = '';
      this.editing = true;
      this.loadAnnotationCounts();
    },
    loadAnnotationCounts() {
      let self = this;
      this.annotationCounts = {};
      piincHttp.get('projects/' + this.project.slug + '/documents/' + this.file.id + '/annotations/counts').then(function(response) {
        self.annotationCounts = response.data.counts || {};
      }, function() {});
    },
    saveLinks() {
      let self = this;
      this.buttonDisabled = true;
      piincHttp.put('projects/' + this.project.slug + '/evidence/' + this.file.id + '/links', {
        task_ids: this.selectedTaskIds
      }).then(function(response) {
        self.buttonDisabled = false;
        self.editing = false;
        const updated = _.find(response.data.files, item => item.id === self.file.id);
        if (updated) {
          self.file = updated;
          self.loadTasks();
        }
        self.$toastr('success', self.$t('projects.engagement.evidence.links_saved'));
        self.$emit('changed');
      }, function(error) {
        self.buttonDisabled = false;
        self.$toastr('error', error.data && error.data.message ? error.data.message : '');
      });
    }
  }
}
</script>
